import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import WrippleEstimateHorizontal from "../../projects/WrippleEstimateHorizontal";
import BiddingContractStep from "../BiddingContractStep";
import BiddingBriefStep from "../BiddingBriefStep";
import BiddingTalentStep from "../BiddingTalentStep";
import BiddingCandidatesStep from "../BiddingCandidatesStep";
import BiddingProposalsStep from "../BiddingProposalsStep";
import {useAuth} from "../../../providers/AuthProvider";
import InfoTooltip from "../../../components/InfoTooltip";
import {Link, useLocation, useSearchParams} from "react-router-dom";
import {useProject} from "../../../providers/ClientProjectProvider";
import {generatePath} from "react-router-dom";
import { Dropdown } from 'antd';
import Project from "../../../models/Project";
import BiddingManageScopeStep from "../BiddingManageScopeStep";

// For rendering cost estimate
// <WrippleEstimateHorizontal
//     project={props.project}
//     editable={props.project.canEditScope}
//     trans={props.trans}
//     trademark={props.project.isService()}/>

const MobileCostEstimate = ({project,}) => {
    let price_label = TB.tryTrans('projects.wripple_cost_estimate.price_label'),
        budget_label = TB.tryTrans('projects.wripple_cost_estimate.budget_label');

    if (!project) {
        return null;
    }

    const [budgetRange, setBudgetRange] = useState(project.budgetRange());
    const [projectEstimate, setProjectEstimate] = useState(project.hasEstimate() ? project.shortEstimate() : '---');

    useEffect(() => {
        return () => {
            if (project) {
                setBudgetRange(project.budgetRange())
            }
        };
    }, [project.min_budget, project.max_budget]);


    useEffect(() => {
        return () => {
            if (project) {
                setProjectEstimate(project.hasEstimate() ? project.shortEstimate() : '---')
            }
        };
    }, [project.platform_cost]);



    return (
        <div className="project-nav-estimate hide-desktop">
            <div className="flex justify-between"
                 style={styles.mobileStyles}>

                <span style={{fontSize: '13px', lineHeight: '17px', verticalAlign: 'middle'}}>
                    {price_label}
                </span>


                <p style={styles.navEstimateRange}>
                    <span className="wripple-gradient-text">
                        {projectEstimate}
                    </span>

                    {project.isService() && (
                        <InfoTooltip placement="left">
                            <div className="flex justify-between" style={{fontSize: 12}}>
                                <span>{price_label}:</span>
                                <span style={{fontWeight: 500}}>
                                    {projectEstimate}
                                </span>

                            </div>

                            <div className="flex justify-between" style={{fontSize: 12}}>
                                <span>{budget_label}:</span>
                                <span style={{fontWeight: 500}}>
                                    {budgetRange}
                                </span>
                            </div>
                            <hr/>
                            <p style={{
                                fontSize: 10,
                                textAlign: 'left',
                                color: 'white'
                            }}>
                                {TB.trans('projects.wripple_cost_estimate.tooltip')}
                            </p>
                        </InfoTooltip>
                    )}
                </p>

            </div>
            <div className="flex justify-between pb-3 mt-2"
                 style={styles.mobileStyles}>

                <span style={{fontSize: '13px', lineHeight: '17px', verticalAlign: 'middle'}}>
                    {budget_label}
                </span>

                <span style={styles.navEstimateRange}>
                    {budgetRange}
                </span>
            </div>
        </div>
    )
}

const BiddingNavbar = props => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const {project, isLoading} = useProject();
    const {auth} = useAuth();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    if (isLoading || !project || !auth) {
        return null
    }

    const biddingSteps = TB.filter(props.steps || [], step => step !== BiddingContractStep);
    const biddingContractStepPath = `${generatePath(
        '/project/bidding/:project_id', {
            project_id: project.id
        })}/${BiddingContractStep.slug}`

    const isOnBiddingContractStep = location?.pathname?.indexOf(biddingContractStepPath) > -1

    let displayedEstimate = useMemo(() => {
        let assignedCost = project.totalPeriodCost(false);
        if (assignedCost) {
            return assignedCost;
        }

        let estCost = project.platform_cost;

        if (!project.hasEstimate()
            && !TB.isNaN(parseInt(project.platform_cost))
            && parseInt(project.platform_cost) > 0) {
            estCost = project.platform_cost;
        }

        return parseInt(estCost)
    }, [project.totalHourlyCost?.(), project.platform_cost]);


    const steps = biddingSteps.map((step, index) => {
        const stepPath = `${generatePath('/project/bidding/:project_id', { project_id: project.id })}/${step.slug}`;
        const isActive = location?.pathname?.indexOf(stepPath) > -1;
        const isAccomplished = step.accomplishedForProject(project);
        const title = step.stepNameFor(project);
        let badge = step.stepBadgeFor?.(project);
        let isLocked = false;

        switch (step) {
            case BiddingBriefStep:
                isLocked = !auth?.canViewProjectBrief(project);
                break;
            case BiddingTalentStep:
            case BiddingCandidatesStep:
            case BiddingProposalsStep:
                isLocked = !auth?.canViewTalentForProject(project);
                break;
            case BiddingContractStep:
                isLocked = !auth?.canViewSOWForProject(project);
                break;
        }

        return (
            <button
                type="button"
                key={title}
                onClick={() => {
                    if (isLocked) {
                        return TB.showPermissionError();
                    }

                    return !isActive && props.onStepNavigate(step)
                }}

                className={TB.mapClass('min-w-[140px] relative py-2 px-6 block w-full lg:w-auto text-white border border-[#3F434A]', {
                    'bg-primary-100': isActive,
                    'bg-charcoal-300': !isActive,
                    'rounded-t-xl lg:rounded-none lg:rounded-l-xl': index === 0,
                })}
            >
                <i className={TB.mapClass('mr-2', {
                        'text-success': !isActive && isAccomplished,
                        'fas fa-check-circle': isAccomplished,
                        'far fa-circle': !isAccomplished,
                    })}
                />

                {title}

                &nbsp;

                {badge && `(${badge})`}

            </button>
        )
    });

    // w-[calc(100vw-1rem)]

    const menu = useMemo(() => {
        if (!project || !(project instanceof Project)) return null;
        const canEditProject = auth?.canEditProjectAndPositions(project);

        return (
            <div className="shadow-lg bg-white px-4 py-6 md:flex flex-wrap gap-x-10 md:w-full gap-y-6 justify-center">
                <div className="flex flex-col gap-4 mb-4 md:mb-0">
                    <p>Created: {TB.moment(project.created_at).fromNow()}</p>
                    <p>Last Active: {TB.moment(project.updated_at).fromNow()}</p>
                    <p>
                        Dates: {project.anticipatedStart().format('MM/DD/YY')} - {project.anticipatedEnd().format('MM/DD/YY')}

                        <Link
                            disabled={props.disabled || project.is_on_hold}
                            className="btn btn-link btn-link-primary inline ml-3"
                            onClick={(e) => {
                                setIsDropdownOpen(false);
                                if (!canEditProject) {
                                    e.preventDefault();
                                    return TB.showPermissionError();
                                }
                            }}
                            to={{
                                ...location,
                                pathname: './brief/role-requirements'
                            }}
                        >
                            Edit
                        </Link>
                    </p>
                </div>
                <div className="flex flex-col gap-4 mb-4 md:mb-0">
                    {(project.isBYO() && (project.isBidding() || project.isActive())) && (
                        <React.Fragment>
                            <p>Positions Opened: {project.sortedPositions()?.length || 0}</p>
                            <p>Positions Filled: {project.filledPositionCount()}/{project.sortedPositions()?.length || 0}</p>
                        </React.Fragment>
                    )}

                    {!TB.isObject(project.team) && (project.isService() && (
                        <React.Fragment>
                            <p>Proposals Requested: {project.shortlisted_candidates_count}</p>
                            <p>Proposals Received: {project.candidate_proposals_received_count}</p>
                        </React.Fragment>
                    ))}

                    {TB.isObject(project.team) ? (
                        <React.Fragment>
                            <p>Assigned Team: {project.team.name}</p>
                            <div className="project-panel-section-details">
                                <p>
                                    <i className="fas fa-address-card mr-2"/>
                                    {project.isBYO()
                                        ? this.translate('panel_team_type_byo', {
                                            total_members: project.team.positions.length
                                        }) : project.team.type}
                                </p>
                                {TB.isObject(project.team.location) && (
                                    <p>
                                        <i className="fas fa-map-marker-alt mr-2"/>
                                        {project.team.location.city}, {project.team.location.state}
                                    </p>
                                )}
                            </div>
                        </React.Fragment>
                    ) : (
                        <p>Interested Candidates: {project.interested_candidates_count || 0}</p>
                    )}
                </div>
                <div className="flex flex-col gap-4 mb-4 md:mb-0">
                    <p>Estimated Price: {TB.formatCurrency(displayedEstimate)}</p>
                    {/*<p>Budget: {project.budgetRange()}</p>*/}
                </div>
                <div className="flex flex-col gap-3 mb-0 border-t border-black pt-4 md:!pt-0 md:!border-t-0 md:mb-0">
                    <p className="flex items-center">
                        {project.is_on_hold
                            ? <i className="fas fa-times-circle text-danger mr-2"/>
                            : <i className="fas fa-check text-success mr-2"/>
                        }
                        {project.is_on_hold ? "On Hold" : "Active Search"}
                    </p>
                    <button
                        className={TB.mapClass('btn btn-link btn-link-primary p-0 text-sm', { 'disabled-edit-link': !canEditProject })}
                        disabled={!project.canPutOnHold()}
                        onClick={async () => {
                            if (!canEditProject) {
                                return TB.showPermissionError();
                            }
                            setIsDropdownOpen(false);
                            project.toggleOnHold();
                        }}
                    >
                        {project.is_on_hold ? "Make active" : "Put on hold"}
                        {!canEditProject && (
                            <i className="fa fa-lock ml-1" style={{fontSize: 13}} />
                        )}
                    </button>

                    <button
                        className={TB.mapClass('btn btn-link btn-link-primary p-0 text-sm', { 'disabled-edit-link': !canEditProject })}
                        disabled={!project.canCancel()}
                        onClick={async () => {
                            if (!canEditProject) {
                                return TB.showPermissionError();
                            }
                            setIsDropdownOpen(false);
                            project.addToCancel();
                        }}
                    >
                        Cancel
                        {project.canCancel() && !canEditProject && (
                            <i className="fa fa-lock ml-1" style={{fontSize: 13}} />
                        )}
                    </button>
                    {auth?.canManageClients() && project.canCancel() && (
                        <button
                            className="btn btn-link btn-link-danger p-0 text-sm"
                            disabled={!project.canCancel() || searchParams.has('closeProject')}
                            onClick={async () => {
                                setIsDropdownOpen(false);
                                let params = new URLSearchParams(searchParams);
                                params.set('closeProject', '');
                                return setSearchParams(params);
                            }}
                        >
                            Close Project
                        </button>
                    )}
                </div>
            </div>
        )
    }, [project, isLoading]);


    return (
        <React.Fragment>
            <div className="bg-black px-2 flex flex-wrap justify-between gap-x-4">

                <div className="flex-1 order-1 m-auto pr-0 px-0 md:px-4 flex-row flex items-center gap-2">
                    <div>
                        <h5 className="text-[9px] text-white uppercase">Project</h5>
                        <p className="text-base text-white">{project.name}</p>

                        <Dropdown
                            menu={{items: []}}
                            dropdownRender={() => menu}
                            trigger={['click']} placement="bottomLeft"
                            onOpenChange={(open) => setIsDropdownOpen(open)} open={isDropdownOpen}>
                            <a onClick={e => e.preventDefault()} className="flex items-center pr-2 text-xs group">
                                <span className="mr-1 text-blue-400 group-hover:text-blue-200">
                                    Project Details
                                </span>

                                <div className="w-0 h-0
                                    border-l-[4px] border-l-transparent
                                    border-t-[5px] border-t-blue-400 group-hover:border-t-blue-200
                                    border-r-[4px] border-r-transparent"
                                />

                            </a>
                        </Dropdown>
                    </div>

                    {auth.canManageClients() && project.isFixedFee() && (
                        <button
                            type="button"
                            onClick={() => {
                                return !location.pathname.includes('manage-scope') && props.onStepNavigate(BiddingManageScopeStep)
                            }}
                            className={TB.mapClass('ml-auto min-w-[140px] py-2 px-3 block whitespace-nowrap w-full lg:w-auto border border-[#3F434A] rounded-xl', {
                                'bg-gray-800 text-white': !location.pathname.includes('manage-scope'),
                                'bg-primary-100 text-white': location.pathname.includes('manage-scope'),
                            })}
                        >
                            <i className="fas fa-key mr-2"/>
                            Manage Scope
                        </button>
                    )}
                </div>

                <div className="order-3 m-auto basis-full md:basis-auto md:order-2 md:flex-1 lg:flex-none lg:flex justify-center">
                    <div className="lg:inline-flex">
                        {steps}

                        <button
                            type="button"
                            onClick={() => {
                                if (!auth?.canViewSOWForProject(project)) {
                                    return TB.showPermissionError();
                                }

                                return !isOnBiddingContractStep && props.onStepNavigate(BiddingContractStep)
                            }}
                            className={TB.mapClass('min-w-[140px] py-2 px-6 block w-full lg:w-auto text-white border border-[#3F434A] rounded-b-xl lg:rounded-none lg:rounded-r-xl', {
                                'bg-primary-100': isOnBiddingContractStep,
                                'bg-black': !isOnBiddingContractStep,
                            })}
                        >
                            <i className={TB.mapClass('mr-2', {
                                'text-success': !isOnBiddingContractStep && project.isPendingContract(), // && isAccomplished,
                                'fas fa-check-circle': project.isPendingContract() && auth?.canViewSOWForProject(project),
                                // 'far fa-circle': !project.isPendingContract() && auth?.canViewSOWForProject(project),
                                'fas fa-lock': !auth?.canViewSOWForProject(project) || (!project.isPendingContract() && auth?.canViewSOWForProject(project)),
                            })}
                            />
                            {BiddingContractStep.stepNameFor(project)}
                        </button>


                    </div>
                </div>

                <div className="flex-1 order-2 flex items-center justify-end md:order-3 gap-2">
                    <WrippleEstimateHorizontal
                        project={project}
                        editable={(!project.isService() || project.hasConfirmedProjectScope())
                     && auth?.canEditProjectAndPositions(project) && project.canEditScope()}
                        trans={props.trans}
                        trademark={project.isService()}
                        style={{maxWidth: 190, width: '100%', minWidth: 'auto'}}
                        className="navbar-estimate"
                    />
                </div>

            </div>
        </React.Fragment>
    )
};

BiddingNavbar.propTypes = {
    steps: PropTypes.array,
    onStepNavigate: PropTypes.func
};

const styles = {
    navEstimateRange: {
        fontSize: '17px',
        lineHeight: '17px',
        marginBottom: 1,
    },
    mobileStyles: {
        color: 'black',
        alignSelf: 'stretch',
        textAlign: 'right',
        flex: 1
    }
}

export default BiddingNavbar;